import { useUser } from "@customer-tools-web/component-authentication";
import { useI18N } from "@customer-tools-web/component-i18n";
import { Icon } from "@customer-tools-web/component-ui";
import { useGA } from "@customer-tools-web/google-analysis-tracking";
import { Badge } from "antd";
import { Location } from "history";
import * as React from "react";
import { useLocation } from "react-router";
import styled, { css } from "styled-components";
import gql from "graphql-tag";
import { useLazyQuery } from "@apollo/react-hooks";
import { useMarketConfig } from "@customer-tools-web/market-extension-resolver";
import { get } from "lodash";
import * as Cookies from "js-cookie";
import { NetworkStatus } from "apollo-client/core/networkStatus";

interface MessageMenuProps {
  braze: any;
  ctServer: string;
}

const LEAD_COUNT_V2_QUERY = gql`
  query leadCountV2Query {
    leadCountV2 {
      unread
    }
  }
`;

const MessageMenu: React.FC<MessageMenuProps> = (
  props: MessageMenuProps
): React.ReactElement<void> => {
  const isLdmEnabled =
    process.env.REACT_APP_FEATURE_LEAD_MANAGEMENT_V2 === "on";
  const location: Location = useLocation();
  const { triggerEvent } = useGA();
  const { braze, ctServer } = props;
  const user = useUser();
  const { locale, parseToLocaleText } = useI18N();
  const [unread, setUnread] = React.useState(
    isLdmEnabled ? 0 : braze?.getNewLeadCount()
  );

  const config = useMarketConfig();
  const leadUnreadCountCookieName = get(config, "leadUnreadCountCookieName");
  const leadUnreadCount = Cookies.get(leadUnreadCountCookieName!);

  const [
    getLeadCountV2,
    { data: leadCountData, refetch: refetchLeadCountV2, networkStatus, called },
  ] = useLazyQuery(LEAD_COUNT_V2_QUERY, {
    fetchPolicy: "network-only",
    context: { headers: { "accept-language": locale } },
    notifyOnNetworkStatusChange: true,
  });

  const checkLeadUnreadCount = () => {
    const isLoading = [NetworkStatus.loading, NetworkStatus.refetch].includes(
      networkStatus
    );
    if (!isLdmEnabled || isLoading) {
      return;
    }

    if (leadUnreadCount === undefined) {
      called ? refetchLeadCountV2() : getLeadCountV2();
    } else {
      setUnread(Number(leadUnreadCount || 0));
    }
  };

  React.useEffect(() => {
    checkLeadUnreadCount();
  }, [leadUnreadCount]);

  React.useEffect(() => {
    if (isLdmEnabled && !!leadCountData?.leadCountV2) {
      const count = leadCountData.leadCountV2.unread || 0;
      const inFiveMinutes = new Date(new Date().getTime() + 5 * 60 * 1000);
      Cookies.set(leadUnreadCountCookieName!, String(count), {
        expires: inFiveMinutes,
      });
      setUnread(Number(count));
    }
  }, [leadCountData]);

  React.useEffect(() => {
    function countListener() {
      if (isLdmEnabled) {
        called ? refetchLeadCountV2() : getLeadCountV2();
      } else {
        setUnread(braze?.getNewLeadCount());
      }
    }

    // when isLdmEnabled is true, we do not refetch lead count on storage event
    function storageCountListener() {
      if (!isLdmEnabled) {
        countListener();
      }
    }

    if (braze) {
      // to cater multiple tab changes is sync
      window.addEventListener("storage", storageCountListener);
      window.addEventListener("refreshLeadIcon", countListener);

      return function cleanupListener() {
        window.removeEventListener("storage", storageCountListener);
        window.removeEventListener("refreshLeadIcon", countListener);
      };
    }
  }, []);

  const organizationType = React.useMemo(() => {
    if (user?.organisation?.isBranch) {
      return "Branch";
    } else if (user?.organisation?.isAgency) {
      return "Agency";
    }
  }, [user?.organisation]);

  const isActive = () => {
    return location?.pathname?.includes("/leads");
  };

  const onMessageIconClick = React.useCallback(() => {
    triggerEvent({
      event: "navigation",
      event_source: "Header",
      event_category: "Navigation",
      event_action: "Menu",
      event_label: "Leads",
      client_language: locale,
      user_id: user?.id,
      user_name: parseToLocaleText(user.fullName),
      organization_id: user?.organisationId,
      organization_name: parseToLocaleText(user?.organisation?.name),
      organisation_type: organizationType,
      user_role: user?.roleCodes?.map((role) => role?.label)?.join(","),
    });
  }, []);

  const leadsUrl = isLdmEnabled ? "/v2/leads" : "/leads";

  return (
    <BadgeContainer
      href={ctServer + leadsUrl}
      rel="noopener noreferrer"
      onClick={onMessageIconClick}
    >
      <MessageMenuAnchor
        showCount={isLdmEnabled && unread > 0}
        dot={!isLdmEnabled && unread > 0}
        count={!isActive() ? unread : 0}
        overflowCount={99}
        data-test="email-icon-dot"
      >
        <MessageIcon
          active={isActive()}
          size={"1.7rem"}
          data-test="email-icon"
          type={isActive() ? "envelope-on" : "envelope-off"}
        />
      </MessageMenuAnchor>
    </BadgeContainer>
  );
};

const BadgeContainer = styled.a`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  margin-right: 22px;
`;

const MessageMenuAnchor = styled(Badge)<{ showCount?: boolean }>`
  width: 24px;
  height: 16px;
  sup {
    transform: translate(70%, -50%);
    background-color: ${(props) => props.theme.pureGreen};
    font-size: ${(props) => props?.theme?.fontSize?.sup};
    line-height: 14px;
    height: 1.2rem;
    width: 1.2rem;
    left: 1rem;
  }

  ${({ showCount }) =>
    showCount &&
    css`
      sup {
        height: 16px;
        left: unset;
        right: 3px;
        width: auto;
        padding: 0.5px 5px 0 5px;
        font-weight: 600;
      }
    `}
`;

const MessageIcon = styled(Icon)<{ active: boolean }>`
  color: ${(props) =>
    props.active ? props.theme.defaultIconColor : "#76818D"};
`;

export default MessageMenu;
